// Review history list. Loads on mount; shows friendly empty / unconfigured states.
import { useEffect, useState } from 'react';
import { listReviews } from '../lib/history.js';

const SCORE_COLOR = (score) => {
  if (score === null || score === undefined) return 'text-slate-400';
  if (score >= 75) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-300';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
};

const DECISION_BADGE = (decision) => {
  const base = 'inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium';
  switch (decision) {
    case 'Approve':                return `${base} border-emerald-900/40 bg-emerald-900/10 text-emerald-300`;
    case 'Approve with Conditions':return `${base} border-emerald-900/40 bg-emerald-900/10 text-emerald-200`;
    case 'Remediate':              return `${base} border-amber-900/40 bg-amber-900/10 text-amber-200`;
    case 'Escalate':               return `${base} border-orange-900/40 bg-orange-900/10 text-orange-300`;
    case 'Reject':                 return `${base} border-red-900/40 bg-red-900/10 text-red-300`;
    default:                       return `${base} border-navy-700 bg-navy-900 text-slate-400`;
  }
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function HistoryView({ onBack }) {
  const [state, setState] = useState({ status: 'loading', reviews: [], error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const reviews = await listReviews({ limit: 50 });
        if (!cancelled) setState({ status: 'ready', reviews, error: null });
      } catch (err) {
        if (!cancelled) {
          // 503 = Notion not configured. Distinguish for a friendlier message.
          const code = err.status === 503 ? 'unconfigured' : 'error';
          setState({ status: code, reviews: [], error: err.message });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Review history</h1>
          <p className="text-sm text-slate-400">Past reviews persisted to Notion. Newest first.</p>
        </div>
        <button
          onClick={onBack}
          className="rounded-md border border-navy-700 bg-navy-800 px-4 py-2 text-sm hover:border-gold"
        >
          New review
        </button>
      </div>

      {state.status === 'loading' && (
        <div className="rounded-lg border border-navy-700 bg-navy-800 p-8 text-center text-sm text-slate-400">
          Loading reviews…
        </div>
      )}

      {state.status === 'unconfigured' && (
        <div className="space-y-3 rounded-lg border border-amber-900/40 bg-amber-900/10 p-6 text-sm text-amber-100">
          <div className="font-semibold">History is not configured.</div>
          <p className="text-amber-200">
            To persist reviews, create an integration at{' '}
            <a className="underline" href="https://www.notion.so/my-integrations" target="_blank" rel="noreferrer">
              notion.so/my-integrations
            </a>
            , share the "RegDefence Reviews" database with it, then set
            <code className="mx-1 rounded bg-navy-900 px-1 py-0.5 text-amber-200">NOTION_API_KEY</code>
            and
            <code className="mx-1 rounded bg-navy-900 px-1 py-0.5 text-amber-200">NOTION_DB_REGDEFENCE_REVIEWS</code>
            in the environment.
          </p>
          <p className="text-xs text-amber-200/80">{state.error}</p>
        </div>
      )}

      {state.status === 'error' && (
        <div className="rounded-lg border border-red-900/50 bg-red-900/20 p-6 text-sm text-red-300">
          Could not load history: {state.error}
        </div>
      )}

      {state.status === 'ready' && state.reviews.length === 0 && (
        <div className="rounded-lg border border-navy-700 bg-navy-800 p-8 text-center text-sm text-slate-400">
          No reviews saved yet. Run one and it will land here automatically.
        </div>
      )}

      {state.status === 'ready' && state.reviews.length > 0 && (
        <ul className="space-y-2">
          {state.reviews.map((r) => (
            <li
              key={r.id}
              className="rounded-md border border-navy-700 bg-navy-800 p-4 transition hover:border-gold/40"
            >
              <div className="flex items-start gap-4">
                <div className={`shrink-0 text-3xl font-bold ${SCORE_COLOR(r.score)} w-14 text-center`}>
                  {r.score ?? '—'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-white">
                      {r.customerName || 'Untitled'}
                    </span>
                    {r.decision && <span className={DECISION_BADGE(r.decision)}>{r.decision}</span>}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    {formatDate(r.date)}
                    {r.reviewer ? ` · ${r.reviewer}` : ''}
                    {r.fileHash ? ` · sha-${r.fileHash.slice(0, 8)}` : ''}
                  </div>
                  {r.keyFailures && (
                    <div className="mt-2 line-clamp-3 whitespace-pre-line text-xs text-slate-300">
                      {r.keyFailures.split('\n\n')[0]}
                    </div>
                  )}
                </div>
                {r.url && (
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-xs text-gold hover:underline"
                  >
                    Open in Notion ↗
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
